import { useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';

import { BrixLogo } from '@/components/brand/BrixLogo';
import { Button } from '@/components/ui/button';
import { useIsDriver } from '@/hooks/useAudit';
import { useIsSuperadmin } from '@/hooks/admin/useIsSuperadmin';
import { signOut } from '@/lib/auth';

/**
 * AuditLayout — chrome for the Gas Cylinder Rental Fees (/audit/*):
 * the driver Cylinder Audit PWA plus the office's CO₂ tank-rental section.
 *
 * Deliberately NOT the customer AppShell: drivers hold no customer
 * membership, so the shell's active-customer context would come up empty.
 * This is a phone-first standalone surface — navy header bar, content,
 * nothing else.
 *
 * Also installs the PWA plumbing (manifest link + iOS meta) only while an
 * /audit route is mounted, so "Add to Home Screen" from here installs the
 * Cylinder Audit app without turning the whole customer portal into a PWA.
 */

function usePwaManifest() {
  useEffect(() => {
    const links: HTMLLinkElement[] = [];
    const metas: HTMLMetaElement[] = [];

    const manifest = document.createElement('link');
    manifest.rel = 'manifest';
    manifest.href = '/audit.webmanifest';
    document.head.appendChild(manifest);
    links.push(manifest);

    const touchIcon = document.createElement('link');
    touchIcon.rel = 'apple-touch-icon';
    touchIcon.href = '/audit-icon-192.png';
    document.head.appendChild(touchIcon);
    links.push(touchIcon);

    const capable = document.createElement('meta');
    capable.name = 'apple-mobile-web-app-capable';
    capable.content = 'yes';
    document.head.appendChild(capable);
    metas.push(capable);

    const title = document.createElement('meta');
    title.name = 'apple-mobile-web-app-title';
    title.content = 'Cylinder Fees';
    document.head.appendChild(title);
    metas.push(title);

    return () => {
      links.forEach((l) => l.remove());
      metas.forEach((m) => m.remove());
    };
  }, []);
}

export default function AuditLayout() {
  usePwaManifest();
  const { isDriver, loading } = useIsDriver();
  const { isSuperadmin } = useIsSuperadmin();
  const { pathname } = useLocation();
  // The rentals section is a staff desk (tables, three-step run), so it gets
  // the room; the driver audit stays phone-width.
  const onRentals = pathname.startsWith('/audit/rentals');
  // Anything under /audit that is not rentals and not the landing page is the
  // audit side (the picker, or a customer's count).
  const onCount = !onRentals && pathname !== '/audit';

  return (
    <div className="min-h-screen bg-brix-surface-alt">
      <header className="sticky top-0 z-20 bg-brix-primary text-white shadow-card">
        <div className={`mx-auto flex h-14 items-center justify-between px-4 ${onRentals ? 'max-w-5xl' : 'max-w-2xl'}`}>
          <Link to="/audit" className="flex min-w-0 items-center gap-3">
            {/* The real Brix Beverage logo (navy artwork) sits on a white chip so
                it reads on the navy bar — the same treatment the portal header
                gives it with its white left edge. */}
            <span className="flex shrink-0 items-center rounded-md bg-white px-2 py-1">
              <BrixLogo variant="full" size="sm" showTagline={false} label="Brix Beverage" />
            </span>
            <div className="min-w-0 leading-tight">
              <div className="truncate font-display text-sm font-bold tracking-display-tight">
                Gas Cylinder Rental Fees
              </div>
              <div className="text-[11px] text-white/70">
                {onRentals ? 'Management' : onCount ? 'Cylinder audit' : 'Brix Beverage'}
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-1">
          {isSuperadmin && (
            <nav className="mr-2 flex rounded-md bg-white/10 p-0.5 text-xs">
              <Link to="/audit/count" className={`rounded px-2.5 py-1 ${onCount ? 'bg-white text-brix-primary font-semibold' : 'text-white/80 hover:text-white'}`}>Audit</Link>
              <Link to="/audit/rentals" className={`rounded px-2.5 py-1 ${onRentals ? 'bg-white text-brix-primary font-semibold' : 'text-white/80 hover:text-white'}`}>Management</Link>
            </nav>
          )}
          <button
            type="button"
            onClick={() => void signOut()}
            className="rounded-md px-2 py-1 text-xs text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            Sign out
          </button>
          </div>
        </div>
      </header>

      <main className={`mx-auto px-4 pb-24 pt-4 ${onRentals ? 'max-w-5xl' : 'max-w-2xl'}`}>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-24 text-brix-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Checking access…</span>
          </div>
        ) : !isDriver ? (
          <div className="mx-auto mt-16 max-w-sm rounded-xl border border-brix-border bg-brix-surface p-6 text-center shadow-card">
            <ShieldAlert className="mx-auto h-8 w-8 text-brix-warning" />
            <h1 className="mt-3 font-display text-lg font-bold text-brix-ink">
              Driver access required
            </h1>
            <p className="mt-2 text-sm text-brix-muted">
              This tool is for Brix delivery drivers. If you should have access, ask the
              office to enable the driver flag on your account.
            </p>
            <Button asChild variant="secondary" className="mt-4">
              <Link to="/home">Back to the portal</Link>
            </Button>
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}
